import { computed, signal } from '@angular/core';
import { uuid } from '@softline/core';
import { Filter } from '../types/filter';
import { FilterTemplate } from '../types/filter-template';
import { FilterTemplateRepository } from '../repositories/filter-template.repository';
import { PageFilterService } from '../page-filter/services/page-filter.service';
import { normalizeFilter } from '../helper/normalize-filter.function';
import { validateFilterTemplateValue } from '../helper/validate-filter-template.function';

export class FilterTemplateService {

  readonly templates = signal<FilterTemplate[]>([]);
  readonly activeTemplateId = signal<string | null>(null);

  readonly sortedTemplates = computed(() => {
    return [...this.templates()].sort((a, b) => {
      if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  });

  readonly activeTemplate = computed(
    () => this.templates().find((o) => o.id === this.activeTemplateId()) ?? null
  );

  /** true when the committed source states no longer match the active template */
  readonly isDirty = computed(() => {
    const template = this.activeTemplate();
    if (!template) return false;
    for (const source of this.pageFilterService.sources) {
      const key = source.state.config.key;
      if (!key) continue;
      const current = JSON.stringify(normalizeFilter(source.state.state()));
      const saved = JSON.stringify(normalizeFilter(template.values[key] ?? null));
      if (current !== saved) return true;
    }
    return false;
  });

  readonly displayedActive = computed(() => (this.isDirty() ? null : this.activeTemplate()));

  private loading: Promise<void> | null = null;

  constructor(
    private readonly repository: FilterTemplateRepository,
    private readonly pageFilterService: PageFilterService,
    public readonly groupKey: string
  ) {}

  async load(force = false): Promise<void> {
    if (this.loading && !force) return this.loading;
    this.loading = this.repository
      .loadGroup(this.groupKey)
      .then((templates) => this.templates.set(templates));
    return this.loading;
  }

  async createFromDraft(name: string): Promise<FilterTemplate> {
    const values: Record<string, Filter<any> | null> = {};
    for (const state of this.pageFilterService.states()) {
      const key = state.config.key;
      if (!key) continue;
      values[key] = state.state();
    }
    const template: FilterTemplate = {
      id: uuid(),
      name: this.uniqueName(name),
      groupKey: this.groupKey,
      values,
      scope: 'personal',
      readonly: false,
      favorite: false,
      timestamp: new Date().toISOString(),
    };
    const created = await this.repository.create(template);
    this.templates.update((templates) => [...templates, created]);
    this.activeTemplateId.set(created.id);
    return created;
  }

  async rename(id: string, name: string): Promise<void> {
    this.requireWritable(id, 'rename');
    const patched = await this.repository.patch(id, {
      name: this.uniqueName(name, id),
      timestamp: new Date().toISOString(),
    });
    this.replaceLocal(patched);
  }

  async duplicate(id: string, copySuffix: string): Promise<FilterTemplate> {
    const template = this.templates().find((o) => o.id === id);
    if (!template)
      throw new Error(`[FilterTemplateService:duplicate] Template with id ${id} not found`);
    const copy: FilterTemplate = {
      ...template,
      id: uuid(),
      name: this.uniqueName(`${template.name} ${copySuffix}`.trim()),
      scope: 'personal',
      readonly: false,
      favorite: false,
      timestamp: new Date().toISOString(),
    };
    const created = await this.repository.create(copy);
    this.templates.update((templates) => [...templates, created]);
    return created;
  }

  async toggleFavorite(id: string): Promise<void> {
    const template = this.templates().find((o) => o.id === id);
    if (!template)
      throw new Error(`[FilterTemplateService:toggleFavorite] Template with id ${id} not found`);
    const patched = await this.repository.patch(id, { favorite: !template.favorite });
    this.replaceLocal(patched);
  }

  async remove(id: string): Promise<void> {
    this.requireWritable(id, 'remove');
    await this.repository.delete(id);
    this.templates.update((templates) => templates.filter((o) => o.id !== id));
    if (this.activeTemplateId() === id) this.activeTemplateId.set(null);
  }

  /** Applies the template to the sidebar's draft states (commit via the footer). */
  applyToDraft(template: FilterTemplate): void {
    for (const state of this.pageFilterService.states()) {
      const key = state.config.key;
      if (!key || !(key in template.values)) continue;
      state.set(validateFilterTemplateValue(template.values[key], state.config) as Filter<any>);
    }
    this.activeTemplateId.set(template.id);
  }

  /** Applies and commits immediately (menu-bar quick apply). */
  applyAndCommit(template: FilterTemplate): void {
    this.applyToDraft(template);
    this.pageFilterService.applyFilter();
  }

  private replaceLocal(template: FilterTemplate): void {
    this.templates.update((templates) =>
      templates.map((o) => (o.id === template.id ? template : o))
    );
  }

  private requireWritable(id: string, operation: string): FilterTemplate {
    const template = this.templates().find((o) => o.id === id);
    if (!template)
      throw new Error(`[FilterTemplateService:${operation}] Template with id ${id} not found`);
    if (template.readonly)
      throw new Error(`[FilterTemplateService:${operation}] Template '${template.name}' is readonly`);
    return template;
  }

  private uniqueName(name: string, excludeId?: string): string {
    const names = this.templates()
      .filter((o) => o.id !== excludeId)
      .map((o) => o.name);
    if (!names.includes(name)) return name;
    let counter = 2;
    while (names.includes(`${name} (${counter})`)) counter++;
    return `${name} (${counter})`;
  }
}
